import { getUniqueId, filterProps } from '@jsluna/utils';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var ProgressBar = function ProgressBar(_ref) {
  var element = _ref.element,
      className = _ref.className,
      size = _ref.size,
      standalone = _ref.standalone,
      value = _ref.value,
      color = _ref.color,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "className", "size", "standalone", "value", "color"]);

  var Element = element;
  var isDeterminate = value !== undefined;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-progress-bar', size && "ln-c-progress-bar--" + size, standalone && 'ln-c-progress-bar--standalone', color && "ln-c-progress-bar--" + color, isDeterminate && 'ln-c-progress-bar--determinate', className)
  }), isDeterminate && /*#__PURE__*/React.createElement("div", {
    className: "ln-c-progress-bar__fill",
    style: {
      width: value + "%"
    }
  }));
};

ProgressBar.propTypes = {
  /** Allows the top-level element to be customized */
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  className: PropTypes.string,

  /** Use a different theme colour */
  color: PropTypes.oneOf(['dark', 'light']),

  /** Use a different bar height */
  size: PropTypes.oneOf(['small']),

  /** Uses larger bar sizing only on wider screen sizes */
  standalone: PropTypes.bool,

  /** Percentage value of progress if determinate */
  value: PropTypes.number
};
ProgressBar.defaultProps = {
  element: 'div',
  className: undefined,
  color: undefined,
  size: undefined,
  standalone: false,
  value: undefined
};
ProgressBar.displayName = 'ProgressBar';

var ProgressMessage = function ProgressMessage(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-progress-indicator__message', className),
    role: "status",
    "aria-live": "polite"
  }), children);
};

ProgressMessage.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string
};
ProgressMessage.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined
};
ProgressMessage.displayName = 'ProgressMessage';
var SIZE = 48;
var RADIUS = 18;
var THICKNESS = 5;
var CIRCUMFERENCE = (2 * Math.PI * RADIUS).toFixed(2);
var circleProps = {
  cx: SIZE / 2,
  cy: SIZE / 2,
  r: RADIUS,
  fill: 'none',
  strokeWidth: THICKNESS,
  stroke: 'currentColor'
};

var ProgressSpinner = function ProgressSpinner(_ref) {
  var className = _ref.className,
      color = _ref.color,
      size = _ref.size,
      value = _ref.value,
      standalone = _ref.standalone,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "color", "size", "value", "standalone"]);

  var isDeterminate = value !== undefined;
  return /*#__PURE__*/React.createElement("svg", _extends({}, rest, {
    viewBox: "0 0 48 48",
    xmlns: "http://www.w3.org/2000/svg",
    className: classnames('ln-c-progress-spinner', standalone && 'ln-c-progress-spinner--standalone', color && "ln-c-progress-spinner--" + color, size && "ln-c-progress-spinner--" + size, isDeterminate && 'ln-c-progress-spinner--determinate', className)
  }), /*#__PURE__*/React.createElement("circle", _extends({}, circleProps, {
    className: "ln-c-progress-spinner__bg"
  })), /*#__PURE__*/React.createElement("circle", _extends({}, circleProps, {
    className: "ln-c-progress-spinner__fill",
    strokeDasharray: isDeterminate ? CIRCUMFERENCE + "px" : (CIRCUMFERENCE / 4).toFixed(2) + "px " + (CIRCUMFERENCE * 0.75).toFixed(2) + "px",
    strokeDashoffset: isDeterminate ? (100 - value) / 100 * CIRCUMFERENCE : undefined
  })));
};

ProgressSpinner.propTypes = {
  className: PropTypes.string,

  /** Use a different theme colour */
  color: PropTypes.oneOf(['dark', 'light']),

  /** Set className for custom spinner size */
  size: PropTypes.string,

  /** Uses large sizing on larger screen sizes */
  standalone: PropTypes.bool,

  /** Percentage value of progress if determinate */
  value: PropTypes.number
};
ProgressSpinner.defaultProps = {
  className: undefined,
  value: undefined,
  color: undefined,
  size: undefined,
  standalone: false
};
ProgressSpinner.displayName = 'ProgressSpinner';

var isIndicator = function isIndicator(component) {
  return component && component.type && (component.type === ProgressSpinner || component.type === ProgressBar);
};

var ProgressIndicator = /*#__PURE__*/function (_Component) {
  _inheritsLoose(ProgressIndicator, _Component);

  function ProgressIndicator(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.messageId = props.id ? props.id + "-message" : getUniqueId('progress-indicator-');
    _this.focussedEl = null;
    _this.progressEl = null;
    _this.setFocus = _this.setFocus.bind(_assertThisInitialized(_this));
    _this.returnFocus = _this.returnFocus.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = ProgressIndicator.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var loading = this.props.loading;
    if (loading) this.setFocus();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var loading = this.props.loading;
    if (loading && !prevProps.loading) this.setFocus();
    if (!loading && prevProps.loading) this.returnFocus();
  };

  _proto.setFocus = function setFocus() {
    var preventFocus = this.props.preventFocus;

    if (preventFocus) {
      return false;
    }

    this.focussedEl = document.activeElement;

    if (this.progressEl) {
      this.progressEl.focus();
    }

    return true;
  };

  _proto.returnFocus = function returnFocus() {
    var preventFocus = this.props.preventFocus;

    if (preventFocus) {
      return false;
    }

    if (this.focussedEl) {
      this.focussedEl.focus();
    }

    this.focussedEl = null;
    return true;
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props = this.props,
        element = _this$props.element,
        children = _this$props.children,
        className = _this$props.className,
        value = _this$props.value,
        page = _this$props.page,
        loading = _this$props.loading,
        message = _this$props.message,
        indicator = _this$props.indicator,
        preventFocus = _this$props.preventFocus,
        rest = _objectWithoutPropertiesLoose(_this$props, ["element", "children", "className", "value", "page", "loading", "message", "indicator", "preventFocus"]);

    var Element = element;
    var progressProps = {
      className: className,
      role: loading ? 'progressbar' : undefined,
      'aria-valuemin': value ? '0' : undefined,
      'aria-valuemax': value ? '100' : undefined,
      'aria-valuenow': value,
      'aria-busy': loading,
      'aria-label': !message && loading ? 'Loading, please wait.' : undefined,
      'aria-describedby': loading && message ? this.messageId : undefined,
      tabIndex: loading ? 0 : undefined
    };
    var progressMessage = message ? React.cloneElement(message, {
      id: this.messageId
    }) : undefined;
    var progressIndicator = indicator ? React.cloneElement(indicator, {
      value: value
    }) : undefined;

    if (typeof children === 'function') {
      return children({
        progressProps: progressProps,
        message: progressMessage,
        indicator: progressIndicator
      });
    }

    var content = React.Children.map(children, function (child) {
      return !isIndicator(child) && child;
    }).filter(Boolean);
    return /*#__PURE__*/React.createElement(Element, _extends({}, filterProps(rest, ['preventFocus']), progressProps, {
      "aria-label": content.length ? undefined : progressProps['aria-label'],
      "aria-describedby": content.length ? this.messageId : undefined,
      className: classnames('ln-c-progress-indicator', page && 'ln-c-progress-indicator--page', loading && 'is-loading', className),
      ref: function ref(el) {
        _this2.progressEl = el;
      }
    }), loading && (progressIndicator || React.Children.map(children, function (child) {
      return isIndicator(child) && React.cloneElement(child, {
        value: child.props.value || value,
        standalone: child.props.standalone || page
      });
    })), message || !!content.length && /*#__PURE__*/React.createElement(ProgressMessage, {
      id: this.messageId
    }, loading && content));
  };

  return ProgressIndicator;
}(Component);

ProgressIndicator.propTypes = {
  /** Allows the top-level element to be customized */
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),

  /** Can supply a render function to have more control over output */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  id: PropTypes.string,
  className: PropTypes.string,

  /** Percentage value of progress if determinate */
  value: PropTypes.number,

  /** Triggers overlay styling for page level loading */
  page: PropTypes.bool,

  /** Loading message for use with render function, will have relevant props applied */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),

  /** Indicator for use with render function, will have relevant props applied */
  indicator: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),

  /** Whether the indicator is loading or not */
  loading: PropTypes.bool,

  /** Prevents automatically applying focus on the progress element, note this can cause a worse experience for screen reader users */
  preventFocus: PropTypes.bool
};
ProgressIndicator.defaultProps = {
  element: 'div',
  children: undefined,
  id: undefined,
  className: undefined,
  value: undefined,
  page: false,
  message: undefined,
  indicator: undefined,
  loading: false,
  preventFocus: false
};
ProgressIndicator.displayName = 'ProgressIndicator';
export { ProgressBar, ProgressIndicator, ProgressMessage, ProgressSpinner };